import {
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useContext, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiTag, FiX } from "react-icons/fi"

import { TagTemplatesService } from "@/client"
import type {
  TagTemplateCreate,
  TagTemplateUpdate,
  TagTypeEnum,
} from "@/client"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
  VisionSelect,
} from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { TAG_TYPE_OPTIONS } from "@/lib/tagTypeMeta"

export const Route = createFileRoute("/_layout/template-form")({
  component: TemplateForm,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      templateId: search.templateId as string | undefined,
    }
  },
})

interface FormData {
  name: string
  tag_type: TagTypeEnum
  unit?: string
  min_threshold?: number
  max_threshold?: number
  auto_discovery_use_full_epc?: boolean
  auto_discovery_name_prefix?: string
  auto_url_from_epc?: boolean
  // Multi-dimension binding (one column per QDatDroid identifier field).
  tag_type_match?: string
  chip_manufacturer?: string
  cin_brand?: string
  nfc_ic_manufacturer?: string
  // Datalogger-specific knobs. Only meaningful on the two protected
  // templates by default; other templates only show these if their
  // field_layout includes them.
  precision?: "HALF" | "FULL" | ""
  battery_duration_days?: number
  // Per-template alarm routing — promoted only when True.
  alert_on_high_temperature?: boolean
  alert_on_low_temperature?: boolean
  alert_on_generic_temperature?: boolean
  alert_on_low_battery?: boolean
  alert_on_initial_low_battery?: boolean
  alert_on_tamper?: boolean
  description?: string
  category?: string
  location_building?: string
  location_floor?: string
  location_zone?: string
  location_room?: string
  assigned_to?: string
  asset_status?: string
  notes?: string
}

// Tag-type chrome is shared with the list page so the icon a user
// picks here matches what /tag-templates renders for the saved row —
// see frontend/src/lib/tagTypeMeta.ts. Adding a new tag type to the
// backend enum only requires updating that module.
const tagTypes: { value: TagTypeEnum; labelKey: string; icon: string }[] =
  TAG_TYPE_OPTIONS.map(({ value, meta }) => ({
    value,
    labelKey: meta.labelKey,
    icon: meta.icon,
  }))

const alarmFlagKeys = [
  "alert_on_high_temperature",
  "alert_on_low_temperature",
  "alert_on_generic_temperature",
  "alert_on_low_battery",
  "alert_on_initial_low_battery",
  "alert_on_tamper",
] as const
type AlarmFlagKey = (typeof alarmFlagKeys)[number]

function TemplateForm() {
  const { t } = useTranslation(["tags", "common"])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const sc = useSemanticColors()
  const { templateId } = useSearch({ from: "/_layout/template-form" })
  const { currentOrganization } = useContext(OrganizationContext)
  const isEditMode = !!templateId

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      tag_type: "TEMPERATURE",
      // Defaults mirror the backend column defaults so the toggles render
      // in the right state for a brand-new template.
      alert_on_high_temperature: true,
      alert_on_low_temperature: true,
      alert_on_generic_temperature: true,
      alert_on_low_battery: true,
      alert_on_initial_low_battery: false,
      alert_on_tamper: true,
      auto_discovery_use_full_epc: true,
    },
  })

  const selectedType = watch("tag_type")
  // Unit of measurement and the min/max temperature range only apply to
  // temperature-measuring tags. For other types the fields are shown but
  // disabled so it's clear they exist yet aren't relevant.
  const isTempType =
    selectedType === "TEMPERATURE" || selectedType === "TEMPERATURE_DATALOGGER"
  const useFullEpc = watch("auto_discovery_use_full_epc")
  const namePrefix = watch("auto_discovery_name_prefix")

  // Watch all four payload-binding columns so the auto-discovery name
  // preview reflects the operator's current choices live. The same
  // tiered priority as the ingester applies: chip_manufacturer >
  // cin_brand > nfc_ic_manufacturer > tag_type_match.
  const watchedChipManufacturer = watch("chip_manufacturer")
  const watchedCinBrand = watch("cin_brand")
  const watchedNfcIcManufacturer = watch("nfc_ic_manufacturer")
  const watchedTagTypeMatch = watch("tag_type_match")
  const bindingDimensions: {
    fieldKey: AlarmFlagKey | string
    labelKey: string
    value: string
  }[] = [
    {
      fieldKey: "chip_manufacturer",
      labelKey: "tags:form.chipManufacturer",
      value: watchedChipManufacturer?.trim() || "",
    },
    {
      fieldKey: "cin_brand",
      labelKey: "tags:form.cinBrand",
      value: watchedCinBrand?.trim() || "",
    },
    {
      fieldKey: "nfc_ic_manufacturer",
      labelKey: "tags:form.nfcIcManufacturer",
      value: watchedNfcIcManufacturer?.trim() || "",
    },
    {
      fieldKey: "tag_type_match",
      labelKey: "tags:form.tagTypeMatch",
      value: watchedTagTypeMatch?.trim() || "",
    },
  ]
  const winningBinding = bindingDimensions.find((d) => d.value) || null

  // Representative EPC examples for the auto-discovery name preview.
  // SGTIN-96 is 96 bits (24 hex chars); SGTIN-128 is 128 bits (32 hex chars).
  const SGTIN_96_EXAMPLE = "3034256F9560000026EE5164"
  const SGTIN_128_EXAMPLE = "30740000C8E1A2B3D4E5F60718293A4B"
  const previewName = (epc: string): string => {
    const prefix = (namePrefix && namePrefix.trim()) || "Auto-discovered tag"
    const chipSeg = winningBinding?.value ?? ""
    const epcSeg = useFullEpc ? epc : epc.slice(-8)
    return [prefix, chipSeg, epcSeg].filter(Boolean).join(" ")
  }

  // Fetch template data if in edit mode
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["tag-template", templateId],
    queryFn: () =>
      TagTemplatesService.readOrganizationTemplate({
        organisationId: currentOrganization!.id,
        id: templateId!,
      }),
    enabled: isEditMode && !!currentOrganization,
  })

  // Per-category catalog for the four binding combo boxes. Filters each
  // dropdown to only the identifiers that actually belong to that field
  // (tag_type → 4 values, chip_manufacturer → ~58, cin_brand → ~27,
  // nfc_ic_manufacturer → ~109). Falls back silently if the endpoint
  // isn't available yet (older backends).
  const { data: catalog } = useQuery({
    queryKey: ["tag-catalog", currentOrganization?.id],
    queryFn: () =>
      TagTemplatesService.getTagCatalog({
        organisationId: currentOrganization!.id,
      }),
    enabled: !!currentOrganization,
    staleTime: 5 * 60_000,
    retry: false,
  })
  // Sort each category dropdown alphabetically (case-insensitive) so
  // the operator can find a chip family by name without scrolling
  // through the catalog's TID-prefix order.
  const byIdAsc = (a: { id: string }, b: { id: string }) =>
    a.id.localeCompare(b.id, undefined, { sensitivity: "base" })
  const catalogTagTypes = catalog?.by_category.tag_type ?? []
  const catalogChipManufacturers = [
    ...(catalog?.by_category.chip_manufacturer ?? []),
  ].sort(byIdAsc)
  const catalogCinBrands = [...(catalog?.by_category.cin_brand ?? [])].sort(
    byIdAsc,
  )
  const catalogNfcIcManufacturers = [
    ...(catalog?.by_category.nfc_ic_manufacturer ?? []),
  ].sort(byIdAsc)

  // Reset form when template data is loaded
  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        tag_type: template.tag_type,
        unit: template.unit || undefined,
        min_threshold:
          template.min_threshold !== null ? template.min_threshold : undefined,
        max_threshold:
          template.max_threshold !== null ? template.max_threshold : undefined,
        auto_discovery_use_full_epc:
          template.auto_discovery_use_full_epc ?? false,
        auto_discovery_name_prefix:
          template.auto_discovery_name_prefix ?? undefined,
        auto_url_from_epc:
          (template as { auto_url_from_epc?: boolean }).auto_url_from_epc ??
          false,
        tag_type_match: template.tag_type_match ?? undefined,
        chip_manufacturer: template.chip_manufacturer ?? undefined,
        cin_brand: template.cin_brand ?? undefined,
        nfc_ic_manufacturer: template.nfc_ic_manufacturer ?? undefined,
        precision: template.precision ?? "",
        battery_duration_days: template.battery_duration_days ?? undefined,
        alert_on_high_temperature: template.alert_on_high_temperature ?? true,
        alert_on_low_temperature: template.alert_on_low_temperature ?? true,
        alert_on_generic_temperature:
          template.alert_on_generic_temperature ?? true,
        alert_on_low_battery: template.alert_on_low_battery ?? true,
        alert_on_initial_low_battery:
          template.alert_on_initial_low_battery ?? false,
        alert_on_tamper: template.alert_on_tamper ?? true,
        description: template.description || undefined,
        category: template.category || undefined,
        location_building: template.location_building || undefined,
        location_floor: template.location_floor || undefined,
        location_zone: template.location_zone || undefined,
        location_room: template.location_room || undefined,
        assigned_to: template.assigned_to || undefined,
        asset_status: template.asset_status || undefined,
        notes: template.notes || undefined,
      })
    }
  }, [template, reset])

  const createTemplateMutation = useMutation({
    mutationFn: async (data: TagTemplateCreate) => {
      const result = await TagTemplatesService.createOrganizationTemplate({
        organisationId: currentOrganization!.id,
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-templates"] })
      showToast.showSuccessToast(t("tags:form.templateCreated"))
      navigate({ to: "/tag-templates" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to create template"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const updateTemplateMutation = useMutation({
    mutationFn: async (data: TagTemplateUpdate) => {
      const result = await TagTemplatesService.updateOrganizationTemplate({
        organisationId: currentOrganization!.id,
        id: templateId!,
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-templates"] })
      queryClient.invalidateQueries({
        queryKey: ["tag-template", templateId],
      })
      showToast.showSuccessToast(t("tags:form.templateUpdated"))
      navigate({ to: "/tag-templates" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update template"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleFormSubmit = (data: FormData) => {
    if (!currentOrganization) {
      showToast.showErrorToast(t("common:org.noOrg"))
      return
    }

    const payload = {
      name: data.name,
      tag_type: data.tag_type,
      unit: data.unit || undefined,
      min_threshold:
        data.min_threshold !== undefined ? data.min_threshold : undefined,
      max_threshold:
        data.max_threshold !== undefined ? data.max_threshold : undefined,
      auto_discovery_use_full_epc: data.auto_discovery_use_full_epc ?? true,
      auto_discovery_name_prefix:
        data.auto_discovery_name_prefix?.trim() || undefined,
      auto_url_from_epc: data.auto_url_from_epc ?? false,
      tag_type_match: data.tag_type_match?.trim() || undefined,
      chip_manufacturer: data.chip_manufacturer?.trim() || undefined,
      cin_brand: data.cin_brand?.trim() || undefined,
      nfc_ic_manufacturer: data.nfc_ic_manufacturer?.trim() || undefined,
      precision:
        data.precision === "HALF" || data.precision === "FULL"
          ? data.precision
          : undefined,
      battery_duration_days:
        data.battery_duration_days !== undefined &&
        !Number.isNaN(data.battery_duration_days)
          ? Number(data.battery_duration_days)
          : undefined,
      alert_on_high_temperature: data.alert_on_high_temperature ?? true,
      alert_on_low_temperature: data.alert_on_low_temperature ?? true,
      alert_on_generic_temperature: data.alert_on_generic_temperature ?? true,
      alert_on_low_battery: data.alert_on_low_battery ?? true,
      alert_on_initial_low_battery: data.alert_on_initial_low_battery ?? false,
      alert_on_tamper: data.alert_on_tamper ?? true,
      description: data.description || undefined,
      category: data.category || undefined,
      location_building: data.location_building || undefined,
      location_floor: data.location_floor || undefined,
      location_zone: data.location_zone || undefined,
      location_room: data.location_room || undefined,
      assigned_to: data.assigned_to || undefined,
      asset_status: data.asset_status || undefined,
      notes: data.notes || undefined,
    }

    if (isEditMode) {
      updateTemplateMutation.mutate(payload)
    } else {
      createTemplateMutation.mutate({
        ...payload,
        organisation_id: currentOrganization.id,
      })
    }
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (isEditMode && templateLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  // Get default unit based on tag type
  const getDefaultUnit = (type: TagTypeEnum) => {
    switch (type) {
      case "TEMPERATURE":
        return "°C"
      case "HUMIDITY":
        return "%"
      case "GENERIC":
        return ""
      default:
        return ""
    }
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={8}>
        <Box flex={1}>
          <Heading size="xl" color={sc.heading}>
            {isEditMode
              ? t("tags:form.editTemplate")
              : t("tags:form.addTemplate")}
          </Heading>
          <Text color={sc.textMuted} mt={1}>
            {isEditMode
              ? t("tags:form.editTemplateSub")
              : t("tags:form.addTemplateSub")}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/tag-templates" })}
          size="sm"
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "3fr 1fr" }} gap={6}>
        <Box>
          <VStack gap={6} as="form" onSubmit={handleSubmit(handleFormSubmit)}>
            <VisionCard w="100%">
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiTag fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("tags:form.templateConfig")}
                </Heading>
              </Flex>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
                w="100%"
              >
                <Field
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.templateName")}
                  </Text>
                  <VisionInput
                    {...register("name", {
                      required: t("tags:form.templateNameRequired"),
                      minLength: {
                        value: 2,
                        message: t("common:validation.nameMinLength"),
                      },
                    })}
                    placeholder={t("tags:form.templateNamePlaceholder")}
                    size="lg"
                    variant="glass"
                  />
                </Field>

                <Field required>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.tagType")}
                  </Text>
                  <Box
                    as="select"
                    {...register("tag_type", { required: true })}
                    p={3}
                    borderRadius="12px"
                    bg={sc.eventBg}
                    backdropFilter="blur(20px)"
                    border="1px solid rgba(255, 255, 255, 0.2)"
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    w="100%"
                    transition="all 0.2s"
                    _hover={{
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      bg: sc.hoverBg,
                    }}
                    _focus={{
                      outline: "none",
                      borderColor: "brand.200",
                      boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                      bg: sc.activeBg,
                    }}
                  >
                    {tagTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {t(type.labelKey)}
                      </option>
                    ))}
                  </Box>
                </Field>

                <Field>
                  <Text
                    color={isTempType ? sc.heading : sc.textMuted}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.unitLabel")}
                  </Text>
                  <VisionInput
                    {...register("unit")}
                    placeholder={
                      getDefaultUnit(selectedType) ||
                      t("tags:form.unitPlaceholder")
                    }
                    size="lg"
                    variant="glass"
                    disabled={!isTempType}
                  />
                </Field>

                <Box>{/* Empty cell for grid alignment */}</Box>

                <Field
                  invalid={!!errors.min_threshold}
                  errorText={errors.min_threshold?.message}
                >
                  <Text
                    color={isTempType ? sc.heading : sc.textMuted}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t(
                      "tags:form.tempRangeLow",
                      "Temperature Range (Celsius) — Low extent",
                    )}
                  </Text>
                  <VisionInput
                    {...register("min_threshold", {
                      valueAsNumber: true,
                      validate: (value, formValues) => {
                        if (
                          value !== undefined &&
                          formValues.max_threshold !== undefined
                        ) {
                          return (
                            value < formValues.max_threshold ||
                            t("tags:form.minLessThanMax")
                          )
                        }
                        return true
                      },
                    })}
                    placeholder={t("tags:form.minPlaceholder")}
                    size="lg"
                    variant="glass"
                    type="number"
                    step="0.01"
                    disabled={!isTempType}
                  />
                </Field>

                <Field
                  invalid={!!errors.max_threshold}
                  errorText={errors.max_threshold?.message}
                >
                  <Text
                    color={isTempType ? sc.heading : sc.textMuted}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t(
                      "tags:form.tempRangeHigh",
                      "Temperature Range (Celsius) — High extent",
                    )}
                  </Text>
                  <VisionInput
                    {...register("max_threshold", {
                      valueAsNumber: true,
                      validate: (value, formValues) => {
                        if (
                          value !== undefined &&
                          formValues.min_threshold !== undefined
                        ) {
                          return (
                            value > formValues.min_threshold ||
                            t("tags:form.maxGreaterThanMin")
                          )
                        }
                        return true
                      },
                    })}
                    placeholder={t("tags:form.maxPlaceholder")}
                    size="lg"
                    variant="glass"
                    type="number"
                    step="0.01"
                    disabled={!isTempType}
                  />
                </Field>
              </Grid>

              <Grid templateColumns={{ base: "1fr" }} gap={4} w="100%" mt={6}>
                <Field>
                  <Text fontSize="sm" color={sc.heading} mb={1}>
                    {t("tags:form.autoDiscoveryNamePrefix")}
                  </Text>
                  <VisionInput
                    {...register("auto_discovery_name_prefix")}
                    placeholder={t(
                      "tags:form.autoDiscoveryNamePrefixPlaceholder",
                    )}
                    size="lg"
                    variant="glass"
                  />
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t("tags:form.autoDiscoveryNamePrefixHint")}
                  </Text>
                </Field>

                {/* Live preview of the auto-discovery name composition.
                    Explicit about what gets concatenated and which
                    chip-binding dimension wins (the four are mutually
                    exclusive and prioritized: chip_manufacturer >
                    cin_brand > nfc_ic_manufacturer > tag_type_match). */}
                <Box
                  p={3}
                  borderRadius="8px"
                  bg="rgba(255,255,255,0.04)"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.08)"
                >
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    {t("tags:form.autoDiscoveryPreviewTitle")}
                  </Text>

                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("tags:form.autoDiscoveryFormulaLabel")}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={sc.heading}
                    fontFamily="mono"
                    mb={3}
                  >
                    {(namePrefix?.trim() || "Auto-discovered tag") +
                      (winningBinding ? ` ${winningBinding.value}` : "") +
                      (useFullEpc ? " <full EPC>" : " <last 8 hex>")}
                  </Text>

                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("tags:form.autoDiscoveryChipPriorityLabel")}
                  </Text>
                  <VStack align="stretch" gap={1} mb={3}>
                    {bindingDimensions.map((dim, idx) => {
                      const isWinner = winningBinding?.fieldKey === dim.fieldKey
                      return (
                        <Box
                          key={dim.fieldKey}
                          display="flex"
                          gap={2}
                          alignItems="center"
                          opacity={dim.value ? 1 : 0.5}
                        >
                          <Text fontSize="xs" color={sc.textMuted} minW="14px">
                            {idx + 1}.
                          </Text>
                          <Text fontSize="xs" color={sc.heading} minW="180px">
                            {t(dim.labelKey)}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={isWinner ? sc.heading : sc.textMuted}
                            fontFamily="mono"
                          >
                            {dim.value ||
                              `(${t("tags:form.autoDiscoveryUnset")})`}
                          </Text>
                          {isWinner && (
                            <Badge
                              colorPalette="blue"
                              size="sm"
                              variant="surface"
                            >
                              {t("tags:form.autoDiscoveryWinner")}
                            </Badge>
                          )}
                        </Box>
                      )
                    })}
                  </VStack>

                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("tags:form.autoDiscoveryEpcChoice")}{" "}
                    <Text as="span" color={sc.heading} fontWeight="500">
                      {useFullEpc
                        ? t("tags:form.autoDiscoveryEpcFull")
                        : t("tags:form.autoDiscoveryEpcShort")}
                    </Text>
                  </Text>

                  <VStack align="stretch" gap={2} mt={3}>
                    <Box>
                      <Text fontSize="xs" color={sc.textMuted}>
                        SGTIN-96 (EPC{" "}
                        <Text as="span" fontFamily="mono">
                          {SGTIN_96_EXAMPLE}
                        </Text>
                        )
                      </Text>
                      <Text fontSize="sm" color={sc.heading} fontFamily="mono">
                        {previewName(SGTIN_96_EXAMPLE)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color={sc.textMuted}>
                        SGTIN-128 (EPC{" "}
                        <Text as="span" fontFamily="mono">
                          {SGTIN_128_EXAMPLE}
                        </Text>
                        )
                      </Text>
                      <Text fontSize="sm" color={sc.heading} fontFamily="mono">
                        {previewName(SGTIN_128_EXAMPLE)}
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </Grid>

              {/* Multi-dimension binding: each combo pulls from the
                  catalog category it represents. Operators can refine
                  any axis independently; templates bind on the most
                  specific dimension that matches an incoming payload. */}
              <Box mt={6}>
                <Text fontSize="sm" fontWeight="600" color={sc.heading} mb={2}>
                  {t("tags:form.bindingSectionTitle")}
                </Text>
                <Text fontSize="xs" color={sc.textMuted} mb={3}>
                  {t("tags:form.bindingSectionHint")}
                </Text>
                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  gap={4}
                  w="100%"
                >
                  <Field>
                    <Text fontSize="sm" color={sc.heading} mb={1}>
                      {t("tags:form.tagTypeMatch")}
                    </Text>
                    <Flex gap={2} align="center">
                      <Box flex="1">
                        <VisionInput
                          {...register("tag_type_match")}
                          placeholder="Opus / Generic / NFC_V / NFC_A"
                          size="lg"
                          variant="glass"
                          list="catalog-tag-type"
                        />
                        <datalist id="catalog-tag-type">
                          {catalogTagTypes.map((e) => (
                            <option key={e.id} value={e.id} />
                          ))}
                        </datalist>
                      </Box>
                      <VisionButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("tags:form.clearBinding")}
                        title={t("tags:form.clearBinding")}
                        onClick={() =>
                          setValue("tag_type_match", "", {
                            shouldDirty: true,
                          })
                        }
                      >
                        <FiX />
                      </VisionButton>
                    </Flex>
                  </Field>
                  <Field>
                    <Text fontSize="sm" color={sc.heading} mb={1}>
                      {t("tags:form.chipManufacturer")}
                    </Text>
                    <Flex gap={2} align="center">
                      <Box flex="1">
                        <VisionInput
                          {...register("chip_manufacturer")}
                          placeholder="Axzon OPUS / Impinj M730 / NXP Ucode 8 / …"
                          size="lg"
                          variant="glass"
                          list="catalog-chip-manufacturer"
                        />
                        <datalist id="catalog-chip-manufacturer">
                          {catalogChipManufacturers.map((e) => (
                            <option key={e.id} value={e.id} />
                          ))}
                        </datalist>
                      </Box>
                      <VisionButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("tags:form.clearBinding")}
                        title={t("tags:form.clearBinding")}
                        onClick={() =>
                          setValue("chip_manufacturer", "", {
                            shouldDirty: true,
                          })
                        }
                      >
                        <FiX />
                      </VisionButton>
                    </Flex>
                  </Field>
                  <Field>
                    <Text fontSize="sm" color={sc.heading} mb={1}>
                      {t("tags:form.cinBrand")}
                    </Text>
                    <Flex gap={2} align="center">
                      <Box flex="1">
                        <VisionInput
                          {...register("cin_brand")}
                          placeholder="RAIN-Alliance CIN brand"
                          size="lg"
                          variant="glass"
                          list="catalog-cin-brand"
                        />
                        <datalist id="catalog-cin-brand">
                          {catalogCinBrands.map((e) => (
                            <option key={e.id} value={e.id} />
                          ))}
                        </datalist>
                      </Box>
                      <VisionButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("tags:form.clearBinding")}
                        title={t("tags:form.clearBinding")}
                        onClick={() =>
                          setValue("cin_brand", "", { shouldDirty: true })
                        }
                      >
                        <FiX />
                      </VisionButton>
                    </Flex>
                  </Field>
                  <Field>
                    <Text fontSize="sm" color={sc.heading} mb={1}>
                      {t("tags:form.nfcIcManufacturer")}
                    </Text>
                    <Flex gap={2} align="center">
                      <Box flex="1">
                        <VisionInput
                          {...register("nfc_ic_manufacturer")}
                          placeholder="NFC IC vendor (ISO/IEC 7816-6)"
                          size="lg"
                          variant="glass"
                          list="catalog-nfc-ic-manufacturer"
                        />
                        <datalist id="catalog-nfc-ic-manufacturer">
                          {catalogNfcIcManufacturers.map((e) => (
                            <option key={e.id} value={e.id} />
                          ))}
                        </datalist>
                      </Box>
                      <VisionButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t("tags:form.clearBinding")}
                        title={t("tags:form.clearBinding")}
                        onClick={() =>
                          setValue("nfc_ic_manufacturer", "", {
                            shouldDirty: true,
                          })
                        }
                      >
                        <FiX />
                      </VisionButton>
                    </Flex>
                  </Field>
                </Grid>
              </Box>

              {/* Datalogger settings: precision + battery duration.
                  Always visible on the two protected templates (Default
                  catch-all and Temperature Datalogger); on other
                  templates, only shown when the template's field_layout
                  includes them. */}
              {(template?.is_default ||
                template?.is_protected_datalogger ||
                template?.field_layout?.includes("precision") ||
                template?.field_layout?.includes("battery_duration_days") ||
                !isEditMode) && (
                <Box mt={6}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={sc.heading}
                    mb={2}
                  >
                    {t("tags:form.dataloggerSectionTitle")}
                  </Text>
                  <Grid
                    templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                    gap={4}
                    w="100%"
                  >
                    <Field>
                      <Text fontSize="sm" color={sc.heading} mb={1}>
                        {t("tags:form.precision")}
                      </Text>
                      <select
                        {...register("precision")}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          fontSize: "1rem",
                          borderRadius: "12px",
                          background: "rgba(255,255,255,0.04)",
                          color: sc.heading,
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        <option value="">—</option>
                        <option value="HALF">0.5 °C</option>
                        <option value="FULL">1 °C</option>
                      </select>
                      <Text fontSize="xs" color={sc.textMuted} mt={1}>
                        {t("tags:form.precisionHint")}
                      </Text>
                    </Field>
                    <Field>
                      <Text fontSize="sm" color={sc.heading} mb={1}>
                        {t("tags:form.batteryDurationDays")}
                      </Text>
                      <VisionInput
                        {...register("battery_duration_days", {
                          valueAsNumber: true,
                        })}
                        type="number"
                        min={1}
                        placeholder="120"
                        size="lg"
                        variant="glass"
                      />
                      <Text fontSize="xs" color={sc.textMuted} mt={1}>
                        {t("tags:form.batteryDurationDaysHint")}
                      </Text>
                    </Field>
                  </Grid>
                </Box>
              )}

              {/* Per-template alarm routing: ingester only promotes a
                  raw payload alarm to a TagAlert when the matching flag
                  is on. */}
              <Box mt={6}>
                <Text fontSize="sm" fontWeight="600" color={sc.heading} mb={2}>
                  {t("tags:form.alarmRoutingTitle")}
                </Text>
                <Text fontSize="xs" color={sc.textMuted} mb={3}>
                  {t("tags:form.alarmRoutingHint")}
                </Text>
                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  gap={2}
                  w="100%"
                >
                  {alarmFlagKeys.map((flagKey) => (
                    <label
                      key={flagKey}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        color: sc.heading,
                        padding: "0.25rem 0",
                      }}
                    >
                      <input
                        type="checkbox"
                        {...register(flagKey as AlarmFlagKey)}
                        style={{
                          width: 16,
                          height: 16,
                          accentColor: "#34a1dd",
                          cursor: "pointer",
                        }}
                      />
                      <Text fontSize="sm" color={sc.heading}>
                        {t(`tags:form.${flagKey}`)}
                      </Text>
                    </label>
                  ))}
                </Grid>
              </Box>

              <Box mt={6}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: sc.heading,
                  }}
                >
                  <input
                    type="checkbox"
                    {...register("auto_discovery_use_full_epc")}
                    style={{
                      width: 16,
                      height: 16,
                      accentColor: "#34a1dd",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Text fontWeight="500" color={sc.heading}>
                      {t("tags:form.autoDiscoveryUseFullEpc")}
                    </Text>
                    <Text fontSize="xs" color={sc.textMuted}>
                      {t("tags:form.autoDiscoveryUseFullEpcHint")}
                    </Text>
                  </Box>
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: sc.heading,
                    marginTop: "0.75rem",
                  }}
                >
                  <input
                    type="checkbox"
                    {...register("auto_url_from_epc")}
                    style={{
                      width: 16,
                      height: 16,
                      accentColor: "#34a1dd",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Text fontWeight="500" color={sc.heading}>
                      {t("tags:form.autoUrlFromEpc", "Auto-fill URL from EPC")}
                    </Text>
                    <Text fontSize="xs" color={sc.textMuted}>
                      {t(
                        "tags:form.autoUrlFromEpcHint",
                        "When a bound tag has no URL, derive https://<dpp_host>/<EPC>. Saving with this on backfills every currently-bound tag whose URL is still empty; URLs already set are left alone.",
                      )}
                    </Text>
                  </Box>
                </label>
              </Box>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetInfo")}
              </Heading>
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                w="100%"
              >
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.description")}
                  </Text>
                  <VisionInput
                    {...register("description")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.category")}
                  </Text>
                  <VisionInput
                    {...register("category")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
              </Grid>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetLocation")}
              </Heading>
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                w="100%"
              >
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.building")}
                  </Text>
                  <VisionInput
                    {...register("location_building")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.floor")}
                  </Text>
                  <VisionInput
                    {...register("location_floor")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.zone")}
                  </Text>
                  <VisionInput
                    {...register("location_zone")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.room")}
                  </Text>
                  <VisionInput
                    {...register("location_room")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
              </Grid>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetManagement")}
              </Heading>
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={4}
                w="100%"
              >
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.assignedTo")}
                  </Text>
                  <VisionInput
                    {...register("assigned_to")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.assetStatusField")}
                  </Text>
                  <VisionSelect {...register("asset_status")}>
                    <option value="">{t("common:filter.all")}</option>
                    <option value="in_service">
                      {t("tags:form.statusInService")}
                    </option>
                    <option value="stored">
                      {t("tags:form.statusStored")}
                    </option>
                    <option value="maintenance">
                      {t("tags:form.statusMaintenance")}
                    </option>
                    <option value="retired">
                      {t("tags:form.statusRetired")}
                    </option>
                  </VisionSelect>
                </Field>
                <Field gridColumn={{ md: "span 2" }}>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.notes")}
                  </Text>
                  <VisionInput
                    {...register("notes")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
              </Grid>
            </VisionCard>

            <HStack gap={3} w="100%">
              <VisionButton
                type="button"
                variant="ghost"
                size="lg"
                onClick={() => navigate({ to: "/tag-templates" })}
              >
                {t("common:actions.cancel")}
              </VisionButton>
              <VisionButton
                type="submit"
                variant="brand"
                size="lg"
                flex="1"
                loading={
                  createTemplateMutation.isPending ||
                  updateTemplateMutation.isPending
                }
              >
                {createTemplateMutation.isPending ||
                updateTemplateMutation.isPending
                  ? isEditMode
                    ? t("common:status.updating")
                    : t("common:status.creating")
                  : isEditMode
                    ? t("tags:form.updateTemplate")
                    : t("tags:form.createTemplate")}
              </VisionButton>
            </HStack>
          </VStack>
        </Box>

        {/* ══════ RIGHT — SIDEBAR (25%) ══════ */}
        <VStack gap={6} w="100%" minW={0}>
          {isEditMode && template && (
            <VisionCard>
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.templateConfig")}
              </Heading>
              <VStack align="stretch" gap={3}>
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:form.templateName")}
                  </Text>
                  <Text color={sc.heading} fontSize="sm" mt={1}>
                    {template.name}
                  </Text>
                </Box>
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:form.tagType")}
                  </Text>
                  <Text color={sc.heading} fontSize="sm" mt={1}>
                    {template.tag_type}
                  </Text>
                </Box>
                {template.unit && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:form.unitLabel")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {template.unit}
                    </Text>
                  </Box>
                )}
                <HStack gap={3} w="100%">
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate({ to: "/tag-templates" })}
                  >
                    {t("common:actions.cancel")}
                  </VisionButton>
                  <VisionButton
                    // The sidebar lives OUTSIDE the <form>, so a plain
                    // type="submit" wouldn't fire the form's onSubmit.
                    // Trigger the same handler explicitly.
                    type="button"
                    variant="brand"
                    size="lg"
                    flex="1"
                    onClick={handleSubmit(handleFormSubmit)}
                    loading={
                      createTemplateMutation.isPending ||
                      updateTemplateMutation.isPending
                    }
                  >
                    {createTemplateMutation.isPending ||
                    updateTemplateMutation.isPending
                      ? isEditMode
                        ? t("common:status.updating")
                        : t("common:status.creating")
                      : isEditMode
                        ? t("tags:form.updateTemplate")
                        : t("tags:form.createTemplate")}
                  </VisionButton>
                </HStack>
              </VStack>
            </VisionCard>
          )}
        </VStack>
      </Grid>
    </Box>
  )
}
